import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Substitution } from './review';

export type StoreCoverage = {
  id: number;
  name: string;
  provider: string;
  ingredients_matched: number;
  ingredients_total: number;
  products: number;
  /** Nothing was ever looked up here, which is not the same as 0% coverage. */
  never_refreshed: boolean;
  recipes_total?: number;
  recipes_cookable?: number;
  recipes_blocked?: number;
  /** Recipes that are exactly one match away from being cookable. */
  unlockable?: number;
  gaps?: number;
  /** Blends the shop does not sell but could be assembled from what it does.
   *  Already counted inside recipes_cookable. */
  makeable?: number;
};

export type StockedElsewhere = {
  store: string;
  products: number;
  example: string;
};

export type StoreGap = {
  id: number;
  name: string;
  aliases: string[];
  parent: string;
  /** Recipes for which this is the *last* missing ingredient. */
  unlocks: number;
  blocks: number;
  examples: string[];
  stocked_elsewhere: StockedElsewhere[];
  /** Null for ordinary food, and for a blend nobody has described yet. */
  substitution: Substitution | null;
};

export type StoreGaps = {
  store: { id: number; name: string };
  recipes_total: number;
  recipes_cookable: number;
  unlockable: number;
  gaps: number;
  /** Counted over every gap, not the filtered page. */
  gaps_with_substitution: number;
  makeable: number;
  results: StoreGap[];
};

export function useStoreCoverage(full = false) {
  return useQuery({
    queryKey: ['coverage', full],
    queryFn: () => api<StoreCoverage[]>(`/stores/coverage/${full ? '?full=true' : ''}`),
  });
}

export function useStoreGaps(
  storeId: number | null,
  substitution: 'yes' | 'no' | null = null,
  limit = 40,
) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (substitution) query.set('substitution', substitution);

  return useQuery({
    queryKey: ['store-gaps', storeId, substitution, limit],
    enabled: storeId != null,
    queryFn: () => api<StoreGaps>(`/stores/${storeId}/gaps/?${query}`),
  });
}

export function useStoreVerdict(storeId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ingredient, carried }: { ingredient: number; carried: boolean }) =>
      api(`/stores/${storeId}/verdict/`, {
        method: 'POST',
        body: { ingredient, carried },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-gaps'] });
      queryClient.invalidateQueries({ queryKey: ['coverage'] });
    },
  });
}

export type StoreSearchHit = {
  product_id: string;
  product_name: string;
  size: string;
  regular_price: string | null;
  promo_price: string | null;
  available: boolean;
  confidence: number;
  product_url: string;
  image_url: string;
};

/** Ask a store for products matching a term, live. Nothing is written. */
export function useSearchStore() {
  return useMutation({
    mutationFn: ({ storeId, term }: { storeId: number; term: string }) =>
      api<{ store: { id: number; name: string }; term: string; results: StoreSearchHit[] }>(
        `/stores/${storeId}/search/?q=${encodeURIComponent(term)}`,
      ),
  });
}

export type AttachedProduct = {
  id: number;
  store: string;
  product_name: string;
  size: string;
  regular_price: string | null;
  promo_price: string | null;
  image_url: string;
  product_url: string;
  product_id: string;
  /** False when the shop could not be asked what the pasted link points at. */
  looked_up: boolean;
};

/** Attach a product by hand — either one from the search, or a pasted link. */
export function useAttachProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storeId,
      ...body
    }: {
      storeId: number;
      ingredient: number;
      product_id?: string;
      product_url?: string;
      product_name?: string;
      size?: string;
      regular_price?: string | null;
      promo_price?: string | null;
      image_url?: string;
      term?: string;
    }) => api<AttachedProduct>(`/stores/${storeId}/attach/`, { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review'] });
      queryClient.invalidateQueries({ queryKey: ['store-gaps'] });
      queryClient.invalidateQueries({ queryKey: ['coverage'] });
      // A product attached by hand is the one the list should now buy.
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['plan-costs'] });
    },
  });
}

export type StoreRematch = {
  store_id: number;
  store: string;
  /** The terms actually put to the shop: the name, then whichever other name found something. */
  searched: string[];
  matched: number;
  rejected: number;
  /** Candidates the adjudicator could not be reached about, so left undecided. */
  unjudged: number;
  /** Empty unless this shop could not be read. One shop refusing is not the others' problem. */
  error: string;
};

/** Search every shop for one ingredient again, now.
 *
 * Fired after its names are edited: aliases are what the shops are searched
 * for, so an edit changes the answer and nothing else asks the question until
 * the nightly refresh.
 */
export function useRematchIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ingredient }: { ingredient: number }) =>
      api<{ ingredient: number; matched: number; stores: StoreRematch[] }>(
        '/stores/rematch/',
        { method: 'POST', body: { ingredient } },
      ),
    // Returned rather than fired and forgotten, so the mutation stays pending
    // until the queue has actually refetched. The row is showing "Matching
    // ingredients…" on the strength of that flag, and it must not clear before
    // the matches it promised are on screen.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review'] }),
        queryClient.invalidateQueries({ queryKey: ['store-gaps'] }),
        queryClient.invalidateQueries({ queryKey: ['coverage'] }),
      ]),
  });
}

export function useStores() {
  return useQuery({
    queryKey: ['stores'],
    queryFn: () => api<{ id: number; name: string }[]>('/stores/'),
  });
}

export type StoreHealth = {
  store: string;
  store_id: number;
  /** 'never' when the shop has not been read at all. */
  status: 'never' | 'running' | 'success' | 'partial' | 'failed';
  finished_at: string | null;
  products_updated: number;
  /** Matches the adjudicator could not be reached about, so left unresolved. */
  candidates_unjudged: number;
  errors: string[];
};

/** How the last refresh of each store went.
 *
 * Refreshes run unattended overnight, so a shop that quietly stopped
 * answering shows up as a plan built on stale prices and nothing else.
 */
export function useStoreHealth() {
  return useQuery({
    queryKey: ['store-health'],
    queryFn: () => api<StoreHealth[]>('/stores/health/'),
  });
}
